import { useEffect, useMemo, useRef, useState } from 'react';
import { ICommunity } from '../../../../../networked-shared-interfaces/interfaces-phase2/platform-admin/platform-admin';
import { mapCommunityToSteps } from '../mapCommunityToSteps';
import { StepStatus } from '../types';

interface INetworkPlusStepsState {
	enabled: boolean;
	activeStepId: string;
}

export const useNetworkPlusSteps = (community: ICommunity | null) => {
	const [{ enabled, activeStepId }, setState] = useState<INetworkPlusStepsState>({
		enabled: false,
		activeStepId: 'branch',
	});

	// Tracks whether we have performed the one-time initialisation from the API.
	const hasInitialized = useRef(false);

	// Auto-set enabled on first successful community load.
	useEffect(() => {
		if (community && !hasInitialized.current) {
			hasInitialized.current = true;
			setState(s => ({ ...s, enabled: community.isNetworkedPlus === true }));
		}
	}, [community]);

	// Steps are always derived from the latest community payload — no stale mock state.
	const steps = useMemo(() => mapCommunityToSteps(community), [community]);

	const toggleEnabled = () => {
		setState(s => ({ ...s, enabled: !s.enabled }));
	};

	const selectStep = (id: string) => {
		const step = steps.find(st => st.id === id);
		if (step?.status === 'blocked') return;
		setState(s => ({ ...s, activeStepId: id }));
	};

	// Kept for optimistic in-session updates (e.g. after provision before refetch resolves).
	// After refetch, community will update and steps will recompute automatically.
	const markStepStatus = (stepId: string, status: StepStatus) => {
		// No-op when community drives the steps — refetch is the source of truth.
		// This exists as a hook for callers that want to apply a temporary optimistic state.
		// Actual persistence comes from refetch() in useNetworkPlusCommunity.
		console.debug(`[NetworkPlus] markStepStatus called for "${stepId}" → "${status}" (optimistic; refetch will override)`);
	};

	return {
		enabled,
		activeStepId,
		steps,
		toggleEnabled,
		selectStep,
		markStepStatus,
	};
};
